import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const MOJIBAKE_PATTERN = /[\u00D8\u00D9\u00C3\u00C2\u00D0\u00D1\u0160\u0161\u017D\u017E\u201A-\u201E\u2018-\u201D\u2020-\u2022\u2030\u2039\u203A\u0152\u0153\u0178]/;
const ARABIC_PATTERN = /[\u0600-\u06FF]/;
const WINDOWS_1252_BYTE_BY_CODEPOINT = {
  0x20AC: 0x80,
  0x201A: 0x82,
  0x0192: 0x83,
  0x201E: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02C6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8A,
  0x2039: 0x8B,
  0x0152: 0x8C,
  0x017D: 0x8E,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201C: 0x93,
  0x201D: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02DC: 0x98,
  0x2122: 0x99,
  0x0161: 0x9A,
  0x203A: 0x9B,
  0x0153: 0x9C,
  0x017E: 0x9E,
  0x0178: 0x9F,
};

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

export function repairArabicMojibake(value) {
  if (typeof value !== 'string' || !MOJIBAKE_PATTERN.test(value)) {
    return value;
  }

  try {
    const bytes = Uint8Array.from(Array.from(value, (char) => {
      const codePoint = char.charCodeAt(0);
      return WINDOWS_1252_BYTE_BY_CODEPOINT[codePoint] ?? (codePoint & 0xff);
    }));
    const decoded = new TextDecoder('utf-8').decode(bytes);
    if (!decoded || decoded === value || decoded.includes('\uFFFD')) {
      return value;
    }

    return ARABIC_PATTERN.test(decoded) || /[\u061F\u060C]/.test(decoded) ? decoded : value;
  } catch {
    return value;
  }
}

export function normalizeLocalizedData(value) {
  if (typeof value === 'string') {
    return repairArabicMojibake(value);
  }

  if (Array.isArray(value)) {
    return value.map(normalizeLocalizedData);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entryValue]) => [key, normalizeLocalizedData(entryValue)])
    );
  }

  return value;
}

export const DISPLAY_LOCALE = 'en-US';

function toFiniteNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function toDisplayDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const text = String(value).trim();
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatNumber(value, options = {}) {
  return toFiniteNumber(value).toLocaleString(DISPLAY_LOCALE, options);
}

export function formatMoney(value, currency = 'AED', options = {}) {
  return `${formatNumber(value, { maximumFractionDigits: 2, ...options })} ${currency}`;
}

export function formatDate(value, options = {}) {
  const date = toDisplayDate(value);
  if (!date) return value || '';
  return date.toLocaleDateString(DISPLAY_LOCALE, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options,
  });
}

export function formatTime(value, options = {}) {
  const date = toDisplayDate(value);
  if (!date) return value || '';
  return date.toLocaleTimeString(DISPLAY_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export function buildImageUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  if (pathOrUrl.startsWith('data:') || pathOrUrl.startsWith('blob:')) return pathOrUrl;
  if (!BACKEND_URL) return pathOrUrl;

  const backendOrigin = (() => {
    try {
      return new URL(BACKEND_URL).origin;
    } catch {
      return BACKEND_URL;
    }
  })();

  if (/^https?:\/\//i.test(pathOrUrl)) {
    try {
      const url = new URL(pathOrUrl);
      if (url.origin === backendOrigin) return pathOrUrl;
      if (url.pathname.startsWith('/api/') || url.pathname.startsWith('/web/')) {
        return `${backendOrigin}${url.pathname}${url.search}${url.hash}`;
      }
      return pathOrUrl;
    } catch {
      return pathOrUrl;
    }
  }

  const normalized = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${backendOrigin}${normalized}`;
}
